// useOfflineSync — Global hook that monitors connectivity and drains the
// offline mutation queue when the client comes back online.
//
// Mount this ONCE in ClientLayout. It:
//   1. Tracks navigator.onLine + online/offline events
//   2. When transitioning to online, drains queued logSet / completeWorkout mutations
//   3. Exposes { isOnline, pendingCount } for UI indicators
//   4. Shows toast feedback on sync results

import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import {
  getMutationQueue,
  removeMutationFromQueue,
  getMutationQueueSize,
  queueMutation as persistQueueMutation,
  type QueuedMutation,
} from '@/pages/client/training/sessionPersistence';

export function useOfflineSync() {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [pendingCount, setPendingCount] = useState(() => getMutationQueueSize());
  const draining = useRef(false);
  const utils = trpc.useUtils();

  // tRPC mutation handles — we call these to replay queued operations
  const logSet = trpc.workouts.logSet.useMutation();
  const completeWorkout = trpc.workouts.complete.useMutation();

  // Drain the queue sequentially
  const drainQueue = useCallback(async () => {
    if (draining.current) return;
    const queue = getMutationQueue();
    if (queue.length === 0) return;

    draining.current = true;
    let synced = 0;
    let syncedSets = 0;
    let syncedCompletions = 0;
    let failed = 0;

    // Sort by queuedAt so we replay in order
    const sorted = [...queue].sort((a, b) => a.queuedAt - b.queuedAt);

    for (const mutation of sorted) {
      try {
        if (mutation.type === 'logSet') {
          await logSet.mutateAsync(mutation.payload as any);
        } else if (mutation.type === 'completeWorkout') {
          await completeWorkout.mutateAsync(mutation.payload as any);
        }
        removeMutationFromQueue(mutation.id);
        synced++;
        if (mutation.type === 'logSet') syncedSets++;
        if (mutation.type === 'completeWorkout') syncedCompletions++;
      } catch (err) {
        console.warn('[offlineSync] Failed to sync mutation:', mutation.id, err);
        failed++;
        // Stop on first failure to preserve ordering
        break;
      }
    }

    draining.current = false;
    setPendingCount(getMutationQueueSize());

    if (synced > 0) {
      const syncLabel = syncedCompletions > 0
        ? `${syncedCompletions} workout${syncedCompletions === 1 ? '' : 's'}${syncedSets > 0 ? ` and ${syncedSets} set${syncedSets === 1 ? '' : 's'}` : ''} synced`
        : `${syncedSets} set${syncedSets === 1 ? '' : 's'} synced`;
      toast.success(syncLabel, {
        duration: 3000,
        style: {
          background: '#1a1510',
          border: '1px solid rgba(16,185,129,0.4)',
          color: '#10b981',
        },
      });
      if (syncedCompletions > 0) {
        void utils.checkins.history90.invalidate();
        void utils.checkins.getStreak.invalidate();
        void utils.workouts.myStats.invalidate();
        void utils.workouts.clientWorkouts.invalidate();
        void utils.programs.todayFromProgram.invalidate();
      }
    }
    if (failed > 0) {
      toast.error(`${failed} saved change${failed !== 1 ? 's' : ''} failed to sync. Will retry`, {
        duration: 4000,
      });
    }
  }, [logSet, completeWorkout, utils]);

  // Listen for online/offline transitions
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Small delay to let the network stack stabilize
      setTimeout(() => drainQueue(), 1500);
    };
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Try to drain on mount if we're online and there's a queue
    if (navigator.onLine && getMutationQueueSize() > 0) {
      setTimeout(() => drainQueue(), 2000);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [drainQueue]);

  // Periodically check queue size so the UI stays in sync
  useEffect(() => {
    const interval = setInterval(() => {
      setPendingCount(getMutationQueueSize());
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Helper: queue a mutation and update pending count
  const enqueue = useCallback((type: QueuedMutation['type'], payload: unknown) => {
    persistQueueMutation(type, payload);
    setPendingCount(getMutationQueueSize());
  }, []);

  return { isOnline, pendingCount, drainQueue, enqueue };
}
